import type { Author, ContractedBook } from '@/data/types';

export const LOJALITET_TIKK_INTERVALL_DAGER = 7;
export const LOJALITET_BONUS_AKTIV_PIPELINE = 0.2;
export const LOJALITET_DECAY_FORSOMT = 0.5;
export const LOJALITET_BONUS_KURS_INVESTERING = 0.3;
export const LOJALITET_DAGER_FOR_FORSOMMELSE = 60;
export const LOJALITET_DAGER_KURS_AKTIV = 90;

export const LOJALITET_BONUS_MATCH = 5;
export const LOJALITET_BONUS_SNIK_OK = 2;
export const LOJALITET_BONUS_KURS_FULLFOERT = 8;
export const LOJALITET_BONUS_GOD_ANMELDELSE = 3;
export const LOJALITET_STRAFF_DAARLIG_ANMELDELSE = 5;

export const LOJALITET_TERSKEL_MISFORNOEYD = 30;
export const LOJALITET_TERSKEL_KRITISK = 15;
export const LOJALITET_VARSEL_INTERVALL_DAGER = 30;

export const LOJALITET_FLAG_PREFIKS_MISFORNOEYD = 'lojalitet_misfornoyd_';
export const LOJALITET_FLAG_PREFIKS_KRITISK = 'lojalitet_kritisk_';

export type LojalitetNivaa = 'misfornoyd' | 'lunken' | 'trygg' | 'lojal';

export type LojalitetVisning = {
  nivaa: LojalitetNivaa;
  label: string;
  farge: number;
};

const FARGE_ROD = 0xa83245;
const FARGE_OKER = 0xd9a14b;
const FARGE_SKOG = 0x3d5a4d;

export function lojalitetNivaa(verdi: number): LojalitetNivaa {
  if (verdi <= 30) return 'misfornoyd';
  if (verdi <= 50) return 'lunken';
  if (verdi <= 70) return 'trygg';
  return 'lojal';
}

export function lojalitetVisning(verdi: number): LojalitetVisning {
  const nivaa = lojalitetNivaa(verdi);
  switch (nivaa) {
    case 'misfornoyd':
      return { nivaa, label: 'Misfornøyd', farge: FARGE_ROD };
    case 'lunken':
      return { nivaa, label: 'Lunken', farge: FARGE_OKER };
    case 'trygg':
      return { nivaa, label: 'Trygg', farge: FARGE_OKER };
    case 'lojal':
      return { nivaa, label: 'Lojal', farge: FARGE_SKOG };
  }
}

export function harBokIPipeline(
  forfatter: Author,
  iProduksjon: ReadonlyArray<ContractedBook>,
): boolean {
  for (const bok of iProduksjon) {
    if (bok.authorId !== forfatter.id) continue;
    if (bok.status === 'utgitt') continue;
    return true;
  }
  return false;
}

export function dagerSidenSisteUtgivelse(
  forfatter: Author,
  iProduksjon: ReadonlyArray<ContractedBook>,
  utgitteBoker: ReadonlyArray<ContractedBook>,
  daag: number,
): number | null {
  let nyeste: number | null = null;
  const sjekk = (bok: ContractedBook): void => {
    if (bok.authorId !== forfatter.id) return;
    if (bok.utgivelsesDag === null) return;
    if (nyeste === null || bok.utgivelsesDag > nyeste) {
      nyeste = bok.utgivelsesDag;
    }
  };
  for (const bok of iProduksjon) sjekk(bok);
  for (const bok of utgitteBoker) sjekk(bok);
  if (nyeste === null) return null;
  return Math.max(0, daag - nyeste);
}

export function beregnDeltaForFofatter(params: {
  forfatter: Author;
  iProduksjon: ReadonlyArray<ContractedBook>;
  utgitteBoker: ReadonlyArray<ContractedBook>;
  dag: number;
  decayReduksjon: number;
}): number {
  const { forfatter, iProduksjon, utgitteBoker, dag, decayReduksjon } = params;
  if (forfatter.paaKurs) return 0;

  const aktivPipeline = harBokIPipeline(forfatter, iProduksjon);
  const dagerSidenUtg = dagerSidenSisteUtgivelse(
    forfatter,
    iProduksjon,
    utgitteBoker,
    dag,
  );
  const ingenUtgittNylig =
    dagerSidenUtg === null || dagerSidenUtg >= LOJALITET_DAGER_FOR_FORSOMMELSE;

  let delta = 0;

  if (aktivPipeline) {
    delta += LOJALITET_BONUS_AKTIV_PIPELINE;
    if (
      typeof forfatter.sistePaaKursDag === 'number' &&
      dag - forfatter.sistePaaKursDag <= LOJALITET_DAGER_KURS_AKTIV
    ) {
      delta += LOJALITET_BONUS_KURS_INVESTERING;
    }
  }

  if (!aktivPipeline && ingenUtgittNylig) {
    let decay = LOJALITET_DECAY_FORSOMT;
    const dempning = Math.max(0, Math.min(1, decayReduksjon));
    decay *= 1 - dempning;
    delta -= decay;
  }

  return delta;
}

export function clampLojalitet(verdi: number): number {
  if (verdi < 0) return 0;
  if (verdi > 100) return 100;
  return verdi;
}

export function vektForStjele(forfatter: Author): number {
  return Math.max(1, 100 - forfatter.lojalitet);
}

export function velgVektetForfatter(
  kandidater: ReadonlyArray<Author>,
  rng: () => number,
): Author | null {
  if (kandidater.length === 0) return null;
  const vekter = kandidater.map((f) => vektForStjele(f));
  const total = vekter.reduce((sum, v) => sum + v, 0);
  if (total <= 0) return kandidater[0];
  const treff = rng() * total;
  let akkumulert = 0;
  for (let i = 0; i < kandidater.length; i += 1) {
    akkumulert += vekter[i];
    if (treff < akkumulert) return kandidater[i];
  }
  return kandidater[kandidater.length - 1];
}

export function byggMisfornoeydFlag(forfatterId: string): string {
  return `${LOJALITET_FLAG_PREFIKS_MISFORNOEYD}${forfatterId}`;
}

export function byggKritiskFlag(forfatterId: string): string {
  return `${LOJALITET_FLAG_PREFIKS_KRITISK}${forfatterId}`;
}
